<template>
  <div class="container-fluid">
    <div class="row mt-3">

      <!-- ── Lista de Programas ── -->
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              <vue-feather type="layers" class="me-1"></vue-feather> Programas
            </h6>
            <router-link :to="{ name: 'nuevoPrograma' }" class="btn btn-primary btn-xs">
              <vue-feather type="plus" size="14"></vue-feather> Nuevo
            </router-link>
          </div>

          <!-- Cargando -->
          <div v-if="loadingProgramas" class="text-center py-4">
            <div class="loader-box"><div class="loader-3"></div></div>
            <p class="mt-2 text-muted small">Cargando programas…</p>
          </div>

          <!-- Error -->
          <div v-else-if="errorProgramas" class="card-body">
            <div class="alert alert-danger py-2 small">
              {{ errorProgramas }}
              <button class="btn btn-sm btn-link p-0 ms-2" @click="fetchProgramas">Reintentar</button>
            </div>
          </div>

          <!-- Lista -->
          <div v-else class="list-group list-group-flush" style="overflow-y:auto; max-height:70vh;">
            <div v-if="programas.length === 0" class="text-center py-4 text-muted small">
              No hay programas registrados.
            </div>
            <button
              v-for="prog in programas"
              :key="prog.id"
              type="button"
              class="list-group-item list-group-item-action px-3 py-2"
              :class="{ active: programaSeleccionado && programaSeleccionado.id === prog.id }"
              @click="seleccionarPrograma(prog)"
            >
              <div class="fw-semibold">{{ prog.nombre }}</div>
              <div class="small opacity-75">{{ prog.tipo }} · {{ prog.poblacion_objetivo }}</div>
              <div class="small opacity-75">{{ prog.fecha_creacion }}</div>
            </button>
          </div>
        </div>
      </div>

      <!-- ── Métricas del programa seleccionado ── -->
      <div class="col-md-8">
        <div class="card h-100">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              <vue-feather type="bar-chart-2" class="me-1"></vue-feather>
              Métricas
              <span v-if="programaSeleccionado" class="badge bg-primary ms-2">
                {{ programaSeleccionado.nombre }}
              </span>
            </h6>
            <router-link
              v-if="programaSeleccionado"
              :to="{ name: 'nuevaMetrica', params: { programaId: programaSeleccionado.id }, query: { nombre: programaSeleccionado.nombre } }"
              class="btn btn-success btn-xs"
            >
              <vue-feather type="plus" size="14"></vue-feather> Nueva métrica
            </router-link>
          </div>

          <!-- Sin programa seleccionado -->
          <div v-if="!programaSeleccionado" class="card-body d-flex align-items-center justify-content-center text-muted">
            <div class="text-center">
              <vue-feather type="arrow-left-circle" size="40" class="mb-2 opacity-50"></vue-feather>
              <p>Selecciona un programa para ver sus métricas.</p>
            </div>
          </div>

          <!-- Cargando métricas -->
          <div v-else-if="loadingMetricas" class="card-body text-center py-4">
            <div class="loader-box"><div class="loader-3"></div></div>
            <p class="mt-2 text-muted small">Cargando métricas…</p>
          </div>

          <!-- Error métricas -->
          <div v-else-if="errorMetricas" class="card-body">
            <div class="alert alert-danger py-2 small">
              {{ errorMetricas }}
              <button class="btn btn-sm btn-link p-0 ms-2" @click="fetchMetricas(programaSeleccionado.id)">
                Reintentar
              </button>
            </div>
          </div>

          <!-- Tabla de métricas -->
          <div v-else class="card-body p-0">
            <div v-if="metricas.length === 0" class="text-center py-5 text-muted small">
              Este programa no tiene métricas configuradas.
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Unidad</th>
                    <th>Periodicidad</th>
                    <th>Línea base</th>
                    <th>Meta final</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in metricas" :key="m.id">
                    <td>
                      <div class="fw-semibold">{{ m.nombre_metrica }}</div>
                      <div class="small text-muted">{{ m.definicion }}</div>
                      <div class="small text-muted font-monospace">{{ m.formula }}</div>
                    </td>
                    <td><span class="badge bg-secondary">{{ m.unidad_medida }}</span></td>
                    <td>{{ m.periodicidad }}</td>
                    <td>{{ m.linea_base }}</td>
                    <td>{{ m.meta_final }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>

<script>
import api from "@/axios";
import authService from "@/services/authService";

export default {
  name: "EvaluacionesConfiguracion",

  data() {
    return {
      programas: [],
      loadingProgramas: false,
      errorProgramas: null,
      programaSeleccionado: null,

      metricas: [],
      loadingMetricas: false,
      errorMetricas: null,
    };
  },

  created() {
    this.fetchProgramas();
  },

  methods: {
    async fetchProgramas() {
      this.loadingProgramas = true;
      this.errorProgramas = null;
      try {
        const ud = authService.getUserData();
        const userId = ud?.id ?? ud?.pk;
        const { data } = await api.get(`usuarios/${userId}/programas/`);
        this.programas = Array.isArray(data) ? data : (data.programas ?? data.results ?? []);
      } catch (err) {
        this.errorProgramas = err.response?.data?.detail ?? "Error al cargar los programas.";
      } finally {
        this.loadingProgramas = false;
      }
    },

    seleccionarPrograma(prog) {
      this.programaSeleccionado = prog;
      this.metricas = [];
      this.fetchMetricas(prog.id);
    },

    async fetchMetricas(programaId) {
      this.loadingMetricas = true;
      this.errorMetricas = null;
      try {
        const { data } = await api.get(`metricas-config/${programaId}`);
        this.metricas = Array.isArray(data) ? data : (data.results ?? []);
      } catch (err) {
        this.errorMetricas = err.response?.data?.detail ?? "Error al cargar las métricas.";
      } finally {
        this.loadingMetricas = false;
      }
    },
  },
};
</script>

<style scoped>
.list-group-item.active {
  background-color: #182e50;
  border-color: #182e50;
  color: #fff;
}
</style>
